import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  EnzymeRepository,
  RebalancingRepository,
  RebalancingState,
} from '../../../repository';
import {
  AssetUnitPipe,
  BLOCK_EXPLORER_TOKEN_LINK,
  Chain,
  ChainRepository,
  LiquidityBotRequestConditional,
  LiquidityBotService,
  RfqMaxAmounts,
  SubscriberComponent,
  TokenLogoComponent,
} from 'common';
import { takeUntil } from 'rxjs/operators';
import { RfqInfosService } from '../../../service';
import { BuyEntry, SellEntry } from '../../../model';
import { RfqInterpretation } from './rfq-level.enum';
import { NgIcon } from '@ng-icons/core';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'common-rebalancing-rfq-infos',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AssetUnitPipe,
    TokenLogoComponent,
    NgIcon,
    RouterLink,
  ],
  templateUrl: './rfq-infos.component.html',
  styleUrls: ['./rfq-infos.component.scss'],
})
export class RfqInfosComponent extends SubscriberComponent implements OnInit {
  private static readonly MIN_RFQ_INFO_VALUE = 10_000;
  private static readonly RELOAD_INTERVAL = 10_000;

  protected readonly RfqInterpretation = RfqInterpretation;
  public BLOCK_EXPLORER_TOKEN_LINK = BLOCK_EXPLORER_TOKEN_LINK;

  public isSwap = false;
  public sellEntry: SellEntry | undefined;
  public buyEntry: BuyEntry | undefined;
  public maxAmounts: RfqMaxAmounts | undefined;

  public interpretationColor: 'green' | 'yellow' | 'orange' | 'gray' = 'gray';
  public interpretation: RfqInterpretation | undefined;

  public canRequestLiquidity = false;
  public requestSuccessful: boolean | undefined;

  public chain: Chain | undefined;

  constructor(
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly chainRepository: ChainRepository,
    private readonly enzymeRepository: EnzymeRepository,
    private readonly rfqInfosService: RfqInfosService,
    private readonly liquidityBotService: LiquidityBotService,
  ) {
    super();
  }

  public get rebalancingState(): RebalancingState {
    return this.rebalancingRepository.store.getValue();
  }

  public ngOnInit(): void {
    this.setupEntriesListener();
    this.setupChainListener();
    this.setupCanRequestLiquidityListener();
  }

  private setupEntriesListener(): void {
    this.rebalancingRepository.sellEntries$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(entries => {
        void this.handleChange();
      });
    this.rebalancingRepository.buyEntries$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(entries => {
        void this.handleChange();
      });

    setInterval(() => this.handleChange(), RfqInfosComponent.RELOAD_INTERVAL);
  }

  private async handleChange(): Promise<void> {
    this.isSwap = this.rebalancingRepository.isSwap();
    if (this.isSwap) {
      this.sellEntry = this.rebalancingState.sellEntries.filter(
        entry => !entry.excludeFromRebalancing,
      )[0];

      if (
        this.sellEntry.getValueInUsd() < RfqInfosComponent.MIN_RFQ_INFO_VALUE
      ) {
        this.resetInfos();
        return;
      }

      this.buyEntry = this.rebalancingState.buyEntries[0];

      this.maxAmounts = await this.rfqInfosService.getMaxAmounts(
        this.sellEntry.asset.token,
        this.buyEntry.asset.token,
      );
      this.setInterpretation();
    } else {
      this.resetInfos();
    }
  }

  private setInterpretation(): void {
    if (this.sellEntry && this.maxAmounts) {
      if (this.maxAmounts.maxSellAmount === 0n) {
        this.interpretation = RfqInterpretation.NONE;
        this.interpretationColor = 'gray';
      } else if (
        this.maxAmounts.maxSellAmount <
        this.sellEntry.getAmountInTokenUnits() / 3n
      ) {
        this.interpretation = RfqInterpretation.NOTIFY_MM;
        this.interpretationColor = 'orange';
      } else if (
        this.maxAmounts.maxSellAmount <
        (this.sellEntry.getAmountInTokenUnits() * 110n) / 100n
      ) {
        this.interpretation = RfqInterpretation.SPLIT;
        this.interpretationColor = 'yellow';
      } else {
        this.interpretation = RfqInterpretation.FULL;
        this.interpretationColor = 'green';
      }
    }
  }

  private resetInfos(): void {
    this.isSwap = false;
    this.sellEntry = undefined;
    this.buyEntry = undefined;
    this.maxAmounts = undefined;
    this.interpretation = undefined;
  }

  private setupChainListener(): void {
    this.chainRepository.chain$.subscribe(chain => (this.chain = chain));
  }

  private setupCanRequestLiquidityListener(): void {
    this.enzymeRepository.isTaurus$.subscribe(
      isTaurus => (this.canRequestLiquidity = isTaurus),
    );
  }

  public async onRequestClick(): Promise<void> {
    if (!this.sellEntry || !this.buyEntry || !this.chain) {
      return;
    }

    const request = new LiquidityBotRequestConditional();
    request.sellAsset = this.sellEntry.asset;
    request.buyAsset = this.buyEntry.asset;
    request.usdAmount = this.sellEntry.getValueInUsd();
    request.chain = this.chain;

    this.requestSuccessful =
      await this.liquidityBotService.requestLiquidity(request);
  }
}
